import type { KeyboardEvent } from 'react';
import { isEnterOrSpace } from '../utils/keyboard/keyboardKeys';

// Accessibility enhancement for non-button elements (e.g., <a> tags)
// Handles Enter and Space key events to:
// 1. Prevent default behavior (page scrolling/navigation)
// 2. Trigger click events to match button behavior
// 3. Ensure consistent keyboard navigation
//
// Reference: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/button_role#accessibility_concerns
export const handleAccessibleButtonKeyDown = (
  event:
    | KeyboardEvent<HTMLButtonElement>
    | KeyboardEvent<HTMLAnchorElement>
    | KeyboardEvent<HTMLDivElement>,
  buttonDisabled?: boolean,
) => {
  if (isEnterOrSpace(event.key)) {
    // Prevent default browser behavior (like page scrolling on space press)
    event.preventDefault();

    if (!buttonDisabled) {
      // Create a synthetic MouseEvent to call the onClick handler
      const syntheticEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      });

      // Dispatch the event on the current target
      (event.target as HTMLElement).dispatchEvent(syntheticEvent);
    }
  }
};

// Provides ARIA attributes for non-button elements to ensure they behave like buttons
export const getAriaButtonRoleAttribute = (nonButtonElement?: boolean) => {
  const attributes: { role?: string } = {};
  // Only add role="button" for non-button elements to maintain proper semantics
  if (nonButtonElement === undefined || nonButtonElement) {
    attributes.role = 'button';
  }
  return attributes;
};

// Makes the component focusable when not disabled in modern browsers including Safari
export const getAccessibleTabIndex = (
  disabled: boolean | undefined,
): number => {
  // Return -1 for disabled elements to remove them from the tab order
  // Return 0 for enabled elements to respect the natural tab order
  return disabled ? -1 : 0;
};
